/**
 * Contact Store (Zustand)
 *
 * @deprecated This store mixes data cache with UI state.
 * Migrate to the new architecture:
 *
 * **Data fetching/mutations** → Use TanStack Query hooks:
 * ```tsx
 * import { useContacts, useCreateContact } from '@features/contacts/queries';
 * ```
 *
 * **UI-only state** → Use existing hooks or create contactUIStore:
 * ```tsx
 * import { useContactSelection } from '@features/contacts/hooks';
 * ```
 *
 * This store will be removed in a future version.
 * See: apps/web/docs/ARCHITECTURE_MIGRATION_PLAN.md
 *
 * ---
 * Legacy: Global state management for contact data.
 * All data is read from FastAPI cache (no direct Pipedrive pagination).
 */

import { create } from 'zustand';
import { toast } from '@shared/utils/notifications';
import { fetchContacts, fetchContactById, fetchContactsByOrganization, fetchContactsTree, toggleContactPriority, hideContactAPI, hideContactsBulk, updateContact, deleteContact, batchUpdateContacts, fetchContactSelections, fetchContactFlags, updateContactFlag } from '../api/contactApi';
import type { ContactItem, ContactUpdateRequest } from '@shared/types/api';
import type { OrganizationTreeNode, ContactTreeItem } from '../types/contact.types';
import type { DeduplicationConfig, DeduplicationResult, DuplicateGroup, IgnoredDuplicate, DEFAULT_DEDUP_CONFIG } from '@features/contacts/types/deduplication';
import { findDuplicates } from '@shared/utils/contactDeduplicator';
import { logger } from '@shared/utils/logger';

/**
 * Contact store state and actions
 */
interface ContactStore {
  // State
  contacts: ContactItem[];
  contactTree: OrganizationTreeNode[];
  expandedNodes: Set<number>;
  isLoading: boolean;
  isLoadingTree: boolean;
  error: string | null;

  // Deduplication state
  duplicateGroups: DuplicateGroup[];
  deduplicationConfig: DeduplicationConfig;
  isDeduplicating: boolean;
  ignoredDuplicates: IgnoredDuplicate[];

  // Actions
  fetchContacts: () => Promise<void>;
  fetchById: (id: number) => Promise<ContactItem | null>;
  fetchContactsByOrganization: (orgId: number) => Promise<ContactItem[]>;
  searchContacts: (query: string) => ContactItem[];
  filterByOrganization: (orgId: number) => ContactItem[];
  filterByOwner: (ownerId: number) => ContactItem[];
  hasEmail: () => ContactItem[];
  hasPhone: () => ContactItem[];

  // Tree navigation
  fetchTree: (campaignId?: string, week?: number, day?: number, showHidden?: boolean) => Promise<void>;
  toggleExpandOrg: (orgId: number) => void;

  // Interactive actions
  togglePriority: (contactId: number, campaignId?: string) => Promise<void>;
  hideContact: (contactId: number, campaignId?: string) => Promise<void>;
  hideContactsBulk: (contactIds: number[]) => Promise<void>;
  updateContact: (contactId: number, data: ContactUpdateRequest) => Promise<void>;
  deleteContact: (contactId: number) => Promise<void>;
  batchUpdateSelections: (selectedIds: Set<number>, allContactIds: number[], campaignId: string) => Promise<void>;
  loadContactSelections: (campaignId: string) => Promise<Record<string, number[]>>;
  fetchContactFlags: (campaignId: string) => Promise<{ priority: number[]; hidden: number[] }>;

  // Deduplication actions
  findDuplicates: (config?: Partial<DeduplicationConfig>) => Promise<void>;
  mergeContacts: (primaryId: number, secondaryIds: number[]) => Promise<void>;
  ignoreDuplicate: (contactId1: number, contactId2: number, reason?: string) => void;
  updateDeduplicationConfig: (config: Partial<DeduplicationConfig>) => void;
  clearDuplicates: () => void;

  clearError: () => void;
  reset: () => void;
}

/**
 * Load ignored duplicates from localStorage
 */
function loadIgnoredDuplicates(): IgnoredDuplicate[] {
  try {
    const stored = localStorage.getItem('ignoredDuplicates');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Save ignored duplicates to localStorage
 */
function saveIgnoredDuplicates(ignored: IgnoredDuplicate[]): void {
  try {
    localStorage.setItem('ignoredDuplicates', JSON.stringify(ignored));
  } catch (e) {
    logger.error('[ContactStore] Failed to save ignored duplicates:', e);
  }
}

/**
 * Initial state
 */
const initialState = {
  contacts: [],
  contactTree: [],
  expandedNodes: new Set<number>(),
  isLoading: false,
  isLoadingTree: false,
  error: null,
  duplicateGroups: [],
  deduplicationConfig: {
    threshold: 0.8,
    matchFields: ['name', 'address', 'phone'] as ('name' | 'address' | 'phone' | 'email')[],
    weightings: {
      name: 0.5,
      address: 0.3,
      phone: 0.2,
      email: 0.0,
    },
  } as DeduplicationConfig,
  isDeduplicating: false,
  ignoredDuplicates: loadIgnoredDuplicates(),
};

/**
 * Create contact store
 */
export const useContactStore = create<ContactStore>((set, get) => ({
  ...initialState,

  /**
   * Fetch all contacts from cache
   *
   * No pagination - reads entire cache from FastAPI.
   *
   * @example
   * ```tsx
   * const { fetchContacts } = useContactStore();
   * await fetchContacts();
   * ```
   */
  fetchContacts: async () => {
    set({ isLoading: true, error: null });

    try {
      // Fetch all from cache (no pagination params)
      const result = await fetchContacts();

      set({
        contacts: result.contacts,
        isLoading: false,
      });
    } catch (error) {
      logger.error('[ContactStore] Failed to fetch contacts:', error);
      set({
        error: (error as Error).message || 'Failed to fetch contacts',
        isLoading: false,
      });
    }
  },

  /**
   * Fetch single contact by ID
   *
   * @param id - Contact ID
   * @returns Promise resolving to contact or null
   *
   * @example
   * ```tsx
   * const contact = await fetchById(456);
   * ```
   */
  fetchById: async (id: number): Promise<ContactItem | null> => {
    try {
      const contact = await fetchContactById(id);
      return contact;
    } catch (error) {
      logger.error(`[ContactStore] Failed to fetch contact ${id}:`, error);
      return null;
    }
  },

  /**
   * Fetch contacts by organization ID directly from backend
   *
   * This is more efficient than loading all contacts then filtering client-side.
   * Use this for calendar invite panels or any UI that needs contacts for a single org.
   *
   * @param orgId - Organization ID
   * @returns Promise resolving to contacts for that organization
   *
   * @example
   * ```tsx
   * const contacts = await fetchContactsByOrganization(123);
   * // Returns only contacts belonging to org 123
   * ```
   */
  fetchContactsByOrganization: async (orgId: number): Promise<ContactItem[]> => {
    try {
      const result = await fetchContactsByOrganization(orgId);
      return result.contacts || [];
    } catch (error) {
      logger.error(`[ContactStore] Failed to fetch contacts for organization ${orgId}:`, error);
      return [];
    }
  },

  /**
   * Search contacts by name or email (client-side)
   *
   * @param query - Search query (case-insensitive)
   * @returns Filtered contacts
   *
   * @example
   * ```tsx
   * const results = searchContacts('john');
   * ```
   */
  searchContacts: (query: string): ContactItem[] => {
    const { contacts } = get();
    const lowerQuery = query.toLowerCase();

    return contacts.filter(contact =>
      contact.name.toLowerCase().includes(lowerQuery) ||
      contact.email?.toLowerCase().includes(lowerQuery) ||
      contact.title?.toLowerCase().includes(lowerQuery)
    );
  },

  /**
   * Filter contacts by organization (client-side)
   *
   * @param orgId - Organization ID
   * @returns Filtered contacts
   *
   * @example
   * ```tsx
   * const orgContacts = filterByOrganization(123);
   * ```
   */
  filterByOrganization: (orgId: number): ContactItem[] => {
    const { contacts } = get();

    return contacts.filter(contact =>
      contact.organization?.id === orgId
    );
  },

  /**
   * Filter contacts by owner (client-side)
   *
   * @param ownerId - Owner ID
   * @returns Filtered contacts
   *
   * @example
   * ```tsx
   * const myContacts = filterByOwner(16122048);
   * ```
   */
  filterByOwner: (ownerId: number): ContactItem[] => {
    const { contacts } = get();

    return contacts.filter(contact => contact.owner.id === ownerId);
  },

  /**
   * Filter contacts that have emails (client-side)
   *
   * @returns Contacts with non-null emails
   *
   * @example
   * ```tsx
   * const contactsWithEmail = hasEmail();
   * ```
   */
  hasEmail: (): ContactItem[] => {
    const { contacts } = get();

    return contacts.filter(contact => contact.email && contact.email.trim() !== '');
  },

  /**
   * Filter contacts that have phone numbers (client-side)
   *
   * @returns Contacts with non-null phone numbers
   *
   * @example
   * ```tsx
   * const contactsWithPhone = hasPhone();
   * ```
   */
  hasPhone: (): ContactItem[] => {
    const { contacts } = get();

    return contacts.filter(contact => contact.phone && contact.phone.trim() !== '');
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(initialState);
  },

  /**
   * Fetch contact tree structure for navigation
   *
   * @param campaignId - Optional campaign ID to filter by
   * @param week - Optional week number (1-12)
   * @param day - Optional day number (1-7)
   * @param showHidden - Optional flag to include hidden contacts (default: false)
   *
   * @example
   * ```tsx
   * await fetchTree('CAMPAIGN_20251119_170655', 1, 2);
   * await fetchTree('CAMPAIGN_20251119_170655', 1, 2, true); // Include hidden contacts
   * ```
   */
  fetchTree: async (campaignId?: string, week?: number, day?: number, showHidden?: boolean) => {
    set({ isLoadingTree: true, error: null });

    try {
      const data = await fetchContactsTree(campaignId, week, day, showHidden);

      set({
        contactTree: data.organizations || [],
        isLoadingTree: false,
      });

      // Load expanded state from localStorage
      const expandedKey = campaignId
        ? `contactTreeExpanded_${campaignId}`
        : 'contactTreeExpanded';
      const savedExpanded = localStorage.getItem(expandedKey);
      if (savedExpanded) {
        try {
          const expandedArray = JSON.parse(savedExpanded);
          set({ expandedNodes: new Set(expandedArray) });
        } catch (e) {
          logger.warn('Failed to parse expanded nodes from localStorage:', e);
        }
      }
    } catch (error) {
      logger.error('[ContactStore] Failed to fetch contact tree:', error);
      set({
        error: (error as Error).message || 'Failed to fetch contact tree',
        isLoadingTree: false,
      });
    }
  },

  /**
   * Toggle expand/collapse state of organization node
   *
   * @param orgId - Organization ID
   *
   * @example
   * ```tsx
   * toggleExpandOrg(123);
   * ```
   */
  toggleExpandOrg: (orgId: number) => {
    const { expandedNodes } = get();
    const newExpanded = new Set(expandedNodes);

    if (newExpanded.has(orgId)) {
      newExpanded.delete(orgId);
    } else {
      newExpanded.add(orgId);
    }

    set({ expandedNodes: newExpanded });

    // Persist to localStorage
    localStorage.setItem('contactTreeExpanded', JSON.stringify([...newExpanded]));
  },

  /**
   * Toggle priority flag on contact (optimistic update with campaign-scoped persistence)
   *
   * @param contactId - Contact ID
   * @param campaignId - Optional campaign ID for persistence (if not provided, uses old global endpoint)
   *
   * @example
   * ```tsx
   * // Campaign-scoped (persists to PostgreSQL)
   * await togglePriority(456, 'CAMPAIGN_20251122_120000');
   *
   * // Global (legacy, no persistence)
   * await togglePriority(456);
   * ```
   */
  togglePriority: async (contactId: number, campaignId?: string) => {
    const { contactTree } = get();

    // Find current priority state
    let currentPriority = false;
    for (const org of contactTree) {
      const contact = org.contacts.find(c => c.contact_id === contactId);
      if (contact) {
        currentPriority = contact.priority || false;
        break;
      }
    }

    const newPriority = !currentPriority;

    // 1. Optimistic update (immediate UI)
    const updatedTree = contactTree.map((org) => ({
      ...org,
      contacts: org.contacts.map((c) =>
        c.contact_id === contactId ? { ...c, priority: newPriority } : c
      ),
    }));
    set({ contactTree: updatedTree });

    try {
      if (campaignId) {
        // 2a. Campaign-scoped API call (persists to PostgreSQL)
        await updateContactFlag(campaignId, contactId, 'priority', newPriority);
      } else {
        // 2b. Legacy global API call (no persistence - in-memory only)
        await toggleContactPriority(contactId);
        logger.log('[ContactStore] Priority toggled globally (legacy, no persistence)');
      }
    } catch (error) {
      // 3. Revert on failure
      logger.error('[ContactStore] Failed to toggle priority:', error);
      set({ contactTree });

      const errorMsg = (error as Error).message || 'Failed to update priority';
      toast.error(errorMsg);
      set({
        error: errorMsg,
      });
    }
  },

  /**
   * Hide contact by adding to hidden list (campaign-scoped or global)
   *
   * @param contactId - Contact ID
   * @param campaignId - Optional campaign ID for campaign-scoped hiding (persists to PostgreSQL)
   *
   * @example
   * ```tsx
   * // Campaign-scoped (persists to PostgreSQL, only hides in this campaign)
   * await hideContact(456, 'CAMPAIGN_20251122_120000');
   *
   * // Global (legacy, hides from all campaigns)
   * await hideContact(456);
   * ```
   */
  hideContact: async (contactId: number, campaignId?: string) => {
    try {
      if (campaignId) {
        // Campaign-scoped hiding (persists to PostgreSQL)
        await updateContactFlag(campaignId, contactId, 'hidden', true);
      } else {
        // Global hiding (legacy - affects all campaigns)
        await hideContactAPI(contactId);
      }

      // Refresh tree to remove hidden contact
      const { contactTree } = get();
      const updatedTree = contactTree
        .map((org) => ({
          ...org,
          contacts: org.contacts.filter((c) => c.contact_id !== contactId),
        }))
        .filter((org) => org.contacts.length > 0); // Remove empty orgs

      set({ contactTree: updatedTree });

      toast.success(campaignId ? 'Contact hidden in this campaign' : 'Contact hidden globally');
    } catch (error) {
      logger.error('[ContactStore] Failed to hide contact:', error);
      const errorMsg = (error as Error).message || 'Failed to hide contact';
      toast.error(errorMsg);
      set({
        error: errorMsg,
      });
    }
  },

  /**
   * Bulk hide contacts
   *
   * @param contactIds - Array of contact IDs
   *
   * @example
   * ```tsx
   * await hideContactsBulk([123, 456, 789]);
   * ```
   */
  hideContactsBulk: async (contactIds: number[]) => {
    try {
      // API call via service
      await hideContactsBulk(contactIds);

      // Refresh tree to remove hidden contacts
      const { contactTree } = get();
      const hiddenSet = new Set(contactIds);
      const updatedTree = contactTree
        .map((org) => ({
          ...org,
          contacts: org.contacts.filter((c) => !hiddenSet.has(c.contact_id)),
        }))
        .filter((org) => org.contacts.length > 0); // Remove empty orgs

      set({ contactTree: updatedTree });
    } catch (error) {
      logger.error('[ContactStore] Failed to bulk hide contacts:', error);
      const errorMsg = (error as Error).message || 'Failed to bulk hide contacts';
      toast.error(errorMsg);
      set({
        error: errorMsg,
      });
    }
  },

  /**
   * Update contact details
   *
   * @param contactId - Contact ID
   * @param data - Contact update data (partial fields)
   *
   * @example
   * ```tsx
   * await updateContact(456, { title: 'VP Sales', email: 'new@example.com' });
   * ```
   */
  updateContact: async (contactId: number, data: ContactUpdateRequest) => {
    const { contactTree } = get();

    try {
      // API call via service
      await updateContact(contactId, data);

      // Update in tree if present
      const updatedTree = contactTree.map((org) => ({
        ...org,
        contacts: org.contacts.map((c) =>
          c.contact_id === contactId
            ? { ...c, ...data, name: data.name || c.name, title: data.title || c.title }
            : c
        ),
      }));
      set({ contactTree: updatedTree });

      toast.success('Contact updated successfully');
    } catch (error) {
      logger.error('[ContactStore] Failed to update contact:', error);
      const errorMsg = (error as Error).message || 'Failed to update contact';
      toast.error(errorMsg);
      set({ error: errorMsg });
    }
  },

  /**
   * Delete contact permanently
   *
   * @param contactId - Contact ID
   *
   * @example
   * ```tsx
   * await deleteContact(456);
   * ```
   */
  deleteContact: async (contactId: number) => {
    const { contactTree } = get();

    try {
      // API call via service
      await deleteContact(contactId);

      // Remove from tree
      const updatedTree = contactTree
        .map((org) => ({
          ...org,
          contacts: org.contacts.filter((c) => c.contact_id !== contactId),
        }))
        .filter((org) => org.contacts.length > 0); // Remove empty orgs

      set({ contactTree: updatedTree });

      toast.success('Contact deleted successfully');
    } catch (error) {
      logger.error('[ContactStore] Failed to delete contact:', error);
      const errorMsg = (error as Error).message || 'Failed to delete contact';
      toast.error(errorMsg);
      set({ error: errorMsg });
    }
  },

  /**
   * Batch update contact selections for a campaign
   *
   * Persists contact selections to backend campaign configuration.
   * Creates updates for all contacts (selected and unselected).
   *
   * @param selectedIds - Set of selected contact IDs
   * @param allContactIds - Array of all contact IDs in view
   * @param campaignId - Campaign ID to save selections for
   *
   * @example
   * ```tsx
   * await batchUpdateSelections(
   *   new Set([123, 456]),
   *   [123, 456, 789],
   *   'CAMPAIGN_20251119_170655'
   * );
   * ```
   */
  batchUpdateSelections: async (selectedIds: Set<number>, allContactIds: number[], campaignId: string) => {
    try {
      // Build updates array for all contacts (selected and unselected)
      const updates = allContactIds.map(contactId => ({
        contact_id: contactId,
        is_selected: selectedIds.has(contactId)
      }));

      // API call via service
      await batchUpdateContacts(updates, campaignId);

      toast.success(`Saved selections for ${selectedIds.size} contacts`);
    } catch (error) {
      logger.error('[ContactStore] Failed to save batch selections:', error);
      const errorMsg = (error as Error).message || 'Failed to save selections';
      toast.error(errorMsg);
      set({ error: errorMsg });
    }
  },

  /**
   * Load saved contact selections for a campaign
   *
   * Fetches label_contact_selections from PostgreSQL campaign table.
   * This provides persistent selections that survive browser refresh and work cross-device.
   *
   * @param campaignId - Campaign ID to load selections for
   * @returns Promise resolving to label_contact_selections object
   *
   * @example
   * ```tsx
   * const selections = await loadContactSelections('CAMPAIGN_20251119_170655');
   * // Returns: { "123": [456, 789], "456": [101, 102] }
   * // Keys are org IDs (strings), values are arrays of selected contact IDs
   * ```
   */
  loadContactSelections: async (campaignId: string): Promise<Record<string, number[]>> => {
    try {

      // Fetch from backend
      const response = await fetchContactSelections(campaignId);
      const selections = response.label_contact_selections || {};

      logger.log(`[ContactStore] Loaded ${Object.keys(selections).length} org selections`);

      return selections;
    } catch (error) {
      logger.error('[ContactStore] Failed to load contact selections:', error);
      const errorMsg = (error as Error).message || 'Failed to load contact selections';
      set({ error: errorMsg });
      return {};
    }
  },

  /**
   * Fetch contact flags (priority and hidden) for a campaign
   *
   * Loads contact_flags from PostgreSQL campaign table.
   * This provides persistent flags that survive browser refresh and work cross-device.
   *
   * @param campaignId - Campaign ID to load flags for
   * @returns Promise resolving to contact flags object
   *
   * @example
   * ```tsx
   * const flags = await fetchContactFlags('CAMPAIGN_20251119_170655');
   * // Returns: { priority: [123, 456], hidden: [789] }
   * // priority: array of contact IDs with priority flag
   * // hidden: array of contact IDs with hidden flag
   * ```
   */
  fetchContactFlags: async (campaignId: string): Promise<{ priority: number[]; hidden: number[] }> => {
    try {

      // Fetch from backend
      const response = await fetchContactFlags(campaignId);
      const flags = {
        priority: response.priority || [],
        hidden: response.hidden || []
      };


      return flags;
    } catch (error) {
      logger.error('[ContactStore] Failed to load contact flags:', error);
      const errorMsg = (error as Error).message || 'Failed to load contact flags';
      set({ error: errorMsg });
      return { priority: [], hidden: [] };
    }
  },

  /**
   * Find duplicate contacts using fuzzy matching
   *
   * @param config - Optional partial config to override defaults
   *
   * @example
   * ```tsx
   * await findDuplicates({ threshold: 0.85 });
   * ```
   */
  findDuplicates: async (config?: Partial<DeduplicationConfig>) => {
    const { contacts, deduplicationConfig, ignoredDuplicates } = get();

    set({ isDeduplicating: true, error: null });

    try {
      // Merge config with defaults
      const mergedConfig = { ...deduplicationConfig, ...config };

      // Build ignored pairs set
      const ignoredPairs = new Set(
        ignoredDuplicates.map(
          (d) => `${d.contactId1}-${d.contactId2}`
        )
      );

      // Run deduplication algorithm
      const result = findDuplicates(contacts, mergedConfig, ignoredPairs);

      set({
        duplicateGroups: result.groups,
        isDeduplicating: false,
      });

      if (result.duplicatesFound > 0) {
        toast.success(`Found ${result.duplicatesFound} duplicate group(s)`);
      } else {
        toast.info('No duplicates found');
      }

    } catch (error) {
      logger.error('[ContactStore] Failed to find duplicates:', error);
      const errorMsg = (error as Error).message || 'Failed to find duplicates';
      toast.error(errorMsg);
      set({
        error: errorMsg,
        isDeduplicating: false,
      });
    }
  },

  /**
   * Merge duplicate contacts
   *
   * @param primaryId - Primary contact ID (will be kept)
   * @param secondaryIds - Secondary contact IDs (will be archived)
   *
   * @example
   * ```tsx
   * await mergeContacts(123, [456, 789]);
   * ```
   */
  mergeContacts: async (primaryId: number, secondaryIds: number[]) => {
    const { duplicateGroups, contacts } = get();

    try {
      // TODO: Implement backend merge endpoint
      // For now, just remove secondaryIds from contacts list

      const updatedContacts = contacts.filter(
        (c) => !secondaryIds.includes(c.id)
      );

      // Remove merged group from duplicateGroups
      const updatedGroups = duplicateGroups.filter(
        (group) =>
          !group.contacts.some((c) => c.id === primaryId || secondaryIds.includes(c.id))
      );

      set({
        contacts: updatedContacts,
        duplicateGroups: updatedGroups,
      });

      toast.success(`Merged ${secondaryIds.length} contact(s) into primary contact`);
    } catch (error) {
      logger.error('[ContactStore] Failed to merge contacts:', error);
      const errorMsg = (error as Error).message || 'Failed to merge contacts';
      toast.error(errorMsg);
      set({ error: errorMsg });
    }
  },

  /**
   * Ignore duplicate pair
   *
   * @param contactId1 - First contact ID
   * @param contactId2 - Second contact ID
   * @param reason - Optional reason
   *
   * @example
   * ```tsx
   * ignoreDuplicate(123, 456, 'Different people with same name');
   * ```
   */
  ignoreDuplicate: (contactId1: number, contactId2: number, reason?: string) => {
    const { ignoredDuplicates, duplicateGroups } = get();

    const newIgnored: IgnoredDuplicate = {
      contactId1,
      contactId2,
      ignoredAt: new Date().toISOString(),
      reason,
    };

    const updatedIgnored = [...ignoredDuplicates, newIgnored];

    // Remove from duplicate groups
    const updatedGroups = duplicateGroups.filter(
      (group) =>
        !(
          group.contacts.some((c) => c.id === contactId1) &&
          group.contacts.some((c) => c.id === contactId2)
        )
    );

    set({
      ignoredDuplicates: updatedIgnored,
      duplicateGroups: updatedGroups,
    });

    saveIgnoredDuplicates(updatedIgnored);

    toast.success('Duplicate pair ignored');
  },

  /**
   * Update deduplication configuration
   *
   * @param config - Partial config to merge with existing
   *
   * @example
   * ```tsx
   * updateDeduplicationConfig({ threshold: 0.9 });
   * ```
   */
  updateDeduplicationConfig: (config: Partial<DeduplicationConfig>) => {
    const { deduplicationConfig } = get();

    set({
      deduplicationConfig: { ...deduplicationConfig, ...config },
    });

  },

  /**
   * Clear duplicate groups
   */
  clearDuplicates: () => {
    set({ duplicateGroups: [] });
  },
}));
